import { useEffect, useMemo, useState } from "react";
import {
  Banner, Btn, Card, Chip, CurrencySelect, Divider, Drawer, Dropdown, FolderTree, Icon, Input,
  MoreMenu, PageState, SectionLabel, SegmentedControl, StatTile, StatusPill, Tab, Table, Tabs, T,
  TimeZonePicker, Toggle, type FolderTreeNode, type TableColumn,
} from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { useNav } from "../../shell/router";
import { EmptyState } from "../../shell/composites";
import { toast } from "../../shell/toast";
import { useNodeTree, useNodeKinds, useResolvedSettings } from "./useNodes";
import {
  isTenantRoot, pathSegments, wouldCycle, wouldExceedDepth, subtreeMaxDepth,
  nodeCodeIssue, nodeErrorMessage, isRetryable, readActiveChildrenWarning,
  NODE_DEPTH_CAP, type UINode, type UITreeNode, type UINodeKind,
} from "./nodes-adapter";
import { admin } from "@v0y4ge/api-client";

const adm = admin as unknown as Record<string, (...a: unknown[]) => Promise<unknown>>;

/**
 * Organisation — the tenant's tree of nodes.
 *
 * ── REBUILT AGAIN 2026-08-25 (second pass) ─────────────────────────────────
 *
 * The previous cut made every field editable and stopped there, which fixed the
 * wrong half of the problem. What it still did was PRESENT A RECORD: a grid of
 * columns (kind, level, time zone, locale, currency, path) followed by a list of
 * setting keys. Every one of those is true and none of them answers a question
 * an administrator actually arrives with:
 *
 *   · "Is this branch ready to take bookings, or is something missing?"
 *   · "If I change this value here, WHO does it move?"
 *   · "Why does Zürich behave differently from every other branch?"
 *   · "What time is it there right now?"  (before phoning them)
 *
 * So this pass keeps every write the last one added and puts a layer of
 * MEANING over it:
 *
 *  · **Readiness, not attributes.** `nodeIssues()` turns the record into a
 *    short list of things that are actually wrong or unfinished — a place-class
 *    kind with no address, a node still in setup, active children under an
 *    inactive parent, no kind row at all. Each issue lands you on the field
 *    that fixes it. Tiles at the top are the same checks counted tenant-wide
 *    and double as tree filters.
 *  · **Impact, not inheritance trivia.** Every setting row states how many
 *    nodes below actually receive this value and how many override it, computed
 *    from the flat list's own `settings` layers. "Set on this node" alone told
 *    nobody what a change would do.
 *  · **Divergence is the signal.** Regional facts are shown against the
 *    parent's, so what stands out is what DIFFERS — the reason anyone opens
 *    this screen.
 *  · **A compare view.** One setting, every node, side by side: the fastest
 *    honest answer to "where does behaviour vary".
 *  · **Spacing.** Every panel is `Card padding="content"` (24) inside a grid
 *    with `T.space[5]` gaps and a `T.space[6]` page gutter — no card sits flush
 *    against another or against the viewport edge.
 *
 * No cross-domain counts (services, staff, bookings) appear anywhere here: the
 * node contract carries no such linkage, and a number nobody can trust is worse
 * than an absent one.
 *
 * ── THE FOUR RULES, AS THEY LAND HERE ──────────────────────────────────────
 *
 * 1. No fixed level ladder. Kinds come from `/v1/admin/node-kinds`; root is
 *    `parent_id === null`; depth is a number. `kind.class` is used ONLY as the
 *    guidance metadata it is declared to be — "does an address belong here" —
 *    never for authority or structure.
 * 2. No attribute filters. Scope is the `path` prefix.
 * 3. `node_id` vs `owner_node_id` is not this screen's question.
 * 4. Kind labels are shown resolved.
 *
 * `code` and `kind` stay read-only facts: `UpdateNodeInput` is `.strict()` and
 * declares neither, so offering them as fields would 400 on save.
 */

const CODE_HINT = "Lower-case letters, digits and hyphens.";

/** How a setting is PRESENTED. Unknown keys still render — de-slugged, in a
 *  control matched to the value's type. Inventing copy for a tenant's own key
 *  would be guessing at meaning we do not have. */
const SETTING_META: Record<string, { label: string; hint: string; unit?: string; format?: (v: unknown) => string }> = {
  booking_window_days: {
    label: "Booking window",
    hint: "How far ahead customers can book.",
    unit: "days",
    format: (v) => `${v} days ahead`,
  },
  cancellation_hours: {
    label: "Cancellation cut-off",
    hint: "How close to an appointment a customer may still cancel.",
    unit: "hours",
    format: (v) => `${v} hours before`,
  },
  require_id_check: {
    label: "ID check",
    hint: "Whether staff must verify photo ID at the appointment.",
    format: (v) => (v ? "Required" : "Not required"),
  },
  walk_in_enabled: {
    label: "Walk-ins",
    hint: "Whether customers can be served without an appointment.",
    format: (v) => (v ? "Accepted" : "By appointment only"),
  },
};

const deSlug = (k: string) => k.replace(/[_-]+/g, " ").replace(/^./, (c) => c.toUpperCase());
const settingLabel = (k: string) => SETTING_META[k]?.label ?? deSlug(k);
const settingHint = (k: string) => SETTING_META[k]?.hint ?? null;
function settingPreview(k: string, v: unknown): string {
  const f = SETTING_META[k]?.format;
  if (f) return f(v);
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (v === null || v === undefined) return "—";
  return typeof v === "object" ? JSON.stringify(v) : String(v);
}

type ValueKind = "boolean" | "number" | "string" | "other";
const valueKind = (v: unknown): ValueKind =>
  typeof v === "boolean" ? "boolean" : typeof v === "number" ? "number" : typeof v === "string" ? "string" : "other";

const has = (o: Record<string, unknown>, k: string) => Object.prototype.hasOwnProperty.call(o, k);

const LOCALES = ["en-US", "en-GB", "de-DE", "de-CH", "fr-FR", "fr-CH", "it-IT", "es-ES", "nl-NL", "pt-PT", "sv-SE", "ja-JP"];

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "setup", label: "Setup" },
  { value: "inactive", label: "Inactive" },
] as const;

function statusPill(s: UINode["status"]) {
  if (s === "active") return <StatusPill tone="success" dot size="sm">Active</StatusPill>;
  if (s === "inactive") return <StatusPill tone="neutral" dot size="sm">Inactive</StatusPill>;
  return <StatusPill tone="warning" dot size="sm">Setup</StatusPill>;
}

/** The node's own wall clock. The reason anyone asks for a node's time zone is
 *  to know whether it is open — so show the answer, not the identifier. */
function localTime(tz: string, now: Date): { time: string; day: string } | null {
  try {
    return {
      time: new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit" }).format(now),
      day: new Intl.DateTimeFormat("en-GB", { timeZone: tz, weekday: "short", day: "numeric", month: "short" }).format(now),
    };
  } catch {
    return null;
  }
}

/** Readiness, derived from the record — the difference between "here are your
 *  columns" and "here is what is unfinished". */
interface Issue { id: string; tone: "danger" | "warning"; title: string; body: string; tab?: string }

function nodeIssues(n: UINode, all: UINode[], kinds: UINodeKind[]): Issue[] {
  const out: Issue[] = [];
  const kind = kinds.find((k) => k.key === n.kind) ?? null;
  const children = all.filter((c) => c.parent_id === n.id);

  if (!n.kind) {
    out.push({
      id: "no-kind", tone: "danger", title: "No kind row",
      body: "The kind this node points at is missing, so it has no label anywhere it is listed. Check the kind vocabulary.",
    });
  }
  if (kind?.class === "place" && !n.address_line1 && !n.city) {
    out.push({
      id: "no-address", tone: "warning", title: "A place with no address",
      body: `${kind.label} is a place kind, so customers expect somewhere to go. Nothing is set.`,
      tab: "details",
    });
  }
  if (n.status === "setup") {
    out.push({
      id: "setup", tone: "warning", title: "Still in setup",
      body: "Setup means not yet live. Mark it active when it is ready to take bookings.",
      tab: "details",
    });
  }
  if (n.status === "inactive" && children.some((c) => c.status === "active")) {
    out.push({
      id: "orphans", tone: "warning", title: "Active children under an inactive node",
      body: `${children.filter((c) => c.status === "active").length} of its children are still active. Deactivating never cascades, so they keep running.`,
      tab: "structure",
    });
  }
  if (kind?.class === "place" && !n.phone && !n.email) {
    out.push({
      id: "no-contact", tone: "warning", title: "No way to reach it",
      body: "Neither a phone number nor an email address is set on a node customers are sent to.",
      tab: "details",
    });
  }
  return out;
}

/** The editable half of a node — the descriptive columns PATCH accepts. */
interface Draft {
  name: string;
  status: UINode["status"];
  timezone: string;
  locale: string;
  currency: string;
  country_code: string;
  address_line1: string;
  address_line2: string;
  city: string;
  postcode: string;
  lat: string;
  lng: string;
  phone: string;
  email: string;
  website: string;
  sort_order: string;
}

const toDraft = (n: UINode): Draft => ({
  name: n.name,
  status: n.status,
  timezone: n.timezone,
  locale: n.locale,
  currency: n.currency,
  country_code: n.country_code ?? "",
  address_line1: n.address_line1 ?? "",
  address_line2: n.address_line2 ?? "",
  city: n.city ?? "",
  postcode: n.postcode ?? "",
  lat: n.lat == null ? "" : String(n.lat),
  lng: n.lng == null ? "" : String(n.lng),
  phone: n.phone ?? "",
  email: n.email ?? "",
  website: n.website ?? "",
  sort_order: String(n.sort_order),
});

type TreeFilter = "all" | "attention" | "not-active" | "overrides";

export default function NodeTreeScreen() {
  const nav = useNav();
  const { tree, nodes, loading, error, reload } = useNodeTree();
  const { kinds, loading: kindsLoading } = useNodeKinds();

  const [view, setView] = useState("tree");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [autoExpanded, setAutoExpanded] = useState(false);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<TreeFilter>("all");
  const [tab, setTab] = useState("overview");
  const [saving, setSaving] = useState(false);
  const [compareKey, setCompareKey] = useState<string | null>(null);

  /** One clock for the whole screen, ticking a minute at a time. */
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  const [addUnder, setAddUnder] = useState<string | null>(null);
  const [newNode, setNewNode] = useState({ name: "", code: "", kind: "", timezone: "UTC", locale: "en-US", currency: "USD" });

  /** The ACTIVE_CHILDREN advisory — a 200 with the write already DONE. Its
   *  discriminator is `type`, not `code`. */
  const [advisory, setAdvisory] = useState<{ message: string; children: { id: string; name: string }[] } | null>(null);

  const byId = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);
  const selected = selectedId ? byId.get(selectedId) ?? null : null;
  const { resolved, attribution } = useResolvedSettings(selectedId);

  const [draft, setDraft] = useState<Draft | null>(null);
  /** This node's OWN settings layer, as edited. Sent whole — the write replaces
   *  the layer rather than merging into it. */
  const [own, setOwn] = useState<Record<string, unknown>>({});
  const [newSetting, setNewSetting] = useState({ key: "", kind: "text", value: "" });
  const [adding, setAdding] = useState(false);

  const stamp = selected ? `${selected.id}:${selected.updated_at}` : null;
  useEffect(() => {
    if (!selected) { setDraft(null); setOwn({}); return; }
    setDraft(toDraft(selected));
    setOwn({ ...selected.settings });
    setAdding(false);
    setNewSetting({ key: "", kind: "text", value: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stamp]);

  useEffect(() => {
    if (autoExpanded || !nodes.length) return;
    setExpanded(nodes.map((n) => n.id));
    setSelectedId((cur) => cur ?? nodes.find(isTenantRoot)?.id ?? nodes[0].id);
    setAutoExpanded(true);
  }, [nodes, autoExpanded]);

  const kindLabel = (key: string | null) => (key ? kinds.find((k) => k.key === key)?.label ?? key : null);
  const kindOf = (n: UINode) => kinds.find((k) => k.key === n.kind) ?? null;

  // ── Readiness, tenant-wide ────────────────────────────────────────────────
  const issuesByNode = useMemo(() => {
    const m = new Map<string, Issue[]>();
    nodes.forEach((n) => m.set(n.id, nodeIssues(n, nodes, kinds)));
    return m;
  }, [nodes, kinds]);

  const counts = useMemo(() => ({
    total: nodes.length,
    levels: nodes.reduce((m, n) => Math.max(m, n.depth + 1), 0),
    attention: nodes.filter((n) => (issuesByNode.get(n.id) ?? []).length > 0).length,
    notActive: nodes.filter((n) => n.status !== "active").length,
    overrides: nodes.filter((n) => Object.keys(n.settings).length > 0).length,
  }), [nodes, issuesByNode]);

  /** Which nodes the tree is allowed to show — search AND the tile filter. */
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q && filter === "all") return null;
    return new Set(
      nodes
        .filter((n) => !q || n.name.toLowerCase().includes(q) || n.code.toLowerCase().includes(q))
        .filter((n) =>
          filter === "all" ? true
            : filter === "attention" ? (issuesByNode.get(n.id) ?? []).length > 0
            : filter === "not-active" ? n.status !== "active"
            : Object.keys(n.settings).length > 0)
        .map((n) => n.id),
    );
  }, [query, filter, nodes, issuesByNode]);

  const treeNodes: FolderTreeNode[] = useMemo(() => {
    const toFolder = (t: UITreeNode): FolderTreeNode | null => {
      const kids = t.children.map(toFolder).filter(Boolean) as FolderTreeNode[];
      const hit = !matches || matches.has(t.id);
      if (!hit && !kids.length) return null;
      const full = byId.get(t.id);
      const issues = issuesByNode.get(t.id) ?? [];
      const tz = full ? localTime(full.timezone, now) : null;
      return {
        id: t.id,
        label: (
          <span style={{ display: "inline-flex", alignItems: "center", gap: T.space[2], minWidth: 0 }}>
            <span style={{ fontWeight: full && isTenantRoot(full) ? 700 : 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.name}</span>
            {/* Tenant data as text — never an icon keyed off the value. */}
            {t.kind
              ? <Chip>{kindLabel(t.kind)}</Chip>
              : <StatusPill effect="inert" tone="neutral" size="sm">no kind row</StatusPill>}
            {t.status !== "active" ? statusPill(t.status) : null}
            {issues.length ? (
              <span title={issues.map((i) => i.title).join(" · ")} style={{ display: "inline-flex", alignItems: "center", gap: 3, color: T.orange1, ...T.text.label, fontWeight: 600 }}>
                <Icon.warn size={12} />{issues.length}
              </span>
            ) : null}
            {tz && full && full.timezone !== byId.get(full.parent_id ?? "")?.timezone ? (
              <span style={{ ...T.text.label, fontWeight: 400, color: T.soft, fontFamily: T.fontMono, fontSize: 11 }}>{tz.time}</span>
            ) : null}
          </span>
        ),
        children: kids.length ? kids : undefined,
      };
    };
    return tree.map(toFolder).filter(Boolean) as FolderTreeNode[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, matches, byId, kinds, issuesByNode, now]);

  /** A drop target is only OFFERED when the move is legal — cycle and depth are
   *  both cheaply predictable from `path` and `depth`. */
  const canDropOn = (targetId: string, movedId: string | null): boolean => {
    if (!movedId || movedId === targetId) return false;
    const moved = byId.get(movedId), target = byId.get(targetId);
    if (!moved || !target) return false;
    if (isTenantRoot(moved)) return false; // ROOT_IMMOVABLE
    if (moved.parent_id === target.id) return false;
    if (wouldCycle(moved, target)) return false;
    return !wouldExceedDepth(subtreeMaxDepth(nodes, moved), moved.depth, target.depth);
  };

  const failed = (title: string) => (e: unknown) => {
    const body = (e as { body?: unknown })?.body ?? e;
    toast({ tone: "error", title, subtitle: nodeErrorMessage(body) + (isRetryable(body) ? " This one is safe to retry." : "") });
  };

  const move = (movedId: string, targetId: string) => {
    const moved = byId.get(movedId);
    if (!moved) return;
    adm.reparentNode(movedId, { new_parent_id: targetId })
      .then(() => {
        toast({ tone: "success", title: `Moved ${moved.name}`, subtitle: `Now under ${byId.get(targetId)?.name ?? "its new parent"}.` });
        reload(); // every descendant's path and depth changed
      })
      .catch(failed("Couldn't move that node"));
  };

  const onDrop = (targetId: string) => {
    const movedId = dragId;
    setDropTarget(null);
    setDragId(null);
    if (movedId && canDropOn(targetId, movedId)) move(movedId, targetId);
  };

  const createNode = () => {
    const issue = nodeCodeIssue(newNode.code);
    if (issue) { toast({ tone: "error", title: "That code isn't valid", subtitle: issue }); return; }
    if (!newNode.name.trim()) { toast({ tone: "error", title: "A name is required" }); return; }
    if (!newNode.kind) { toast({ tone: "error", title: "A kind is required", subtitle: "And it's the kind key, not the id." }); return; }
    setSaving(true);
    adm.createNode({
      name: newNode.name.trim(), code: newNode.code, kind: newNode.kind, parent_id: addUnder,
      timezone: newNode.timezone, locale: newNode.locale, currency: newNode.currency,
    })
      .then((raw: unknown) => {
        const created = (raw as { data?: { id?: string } })?.data;
        toast({ tone: "success", title: `Created ${newNode.name.trim()}` });
        setAddUnder(null);
        setNewNode({ name: "", code: "", kind: "", timezone: "UTC", locale: "en-US", currency: "USD" });
        if (created?.id) setSelectedId(created.id);
        reload();
      })
      // An unknown kind returns a 400 whose message LISTS the tenant's keys.
      .catch(failed("Couldn't create that node"))
      .finally(() => setSaving(false));
  };

  /** Retiring a node is PATCH { status }, never a DELETE. Never cascades. */
  const patch = (node: UINode, body: Record<string, unknown>, okTitle: string) => {
    setSaving(true);
    adm.updateNode(node.id, body)
      .then((raw: unknown) => {
        const w = readActiveChildrenWarning(raw);
        if (w) setAdvisory({ message: w.message, children: w.active_children });
        else { setAdvisory(null); toast({ tone: "success", title: okTitle }); }
        reload();
      })
      .catch(failed("Couldn't save that change"))
      .finally(() => setSaving(false));
  };

  // ── The dirty model ───────────────────────────────────────────────────────
  const settingsDirty = !!selected && JSON.stringify(own) !== JSON.stringify(selected.settings);
  const fieldPatch = useMemo(() => {
    if (!selected || !draft) return {} as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    const text = (key: keyof Draft, current: string | null) => {
      const next = draft[key].trim();
      if (next !== (current ?? "")) out[key] = next === "" ? null : next;
    };
    if (draft.name.trim() !== selected.name) out.name = draft.name.trim();
    if (draft.status !== selected.status) out.status = draft.status;
    if (draft.timezone !== selected.timezone) out.timezone = draft.timezone;
    if (draft.locale !== selected.locale) out.locale = draft.locale;
    if (draft.currency !== selected.currency) out.currency = draft.currency;
    if (draft.country_code.trim().toUpperCase() !== (selected.country_code ?? "")) {
      out.country_code = draft.country_code.trim() ? draft.country_code.trim().toUpperCase() : null;
    }
    text("address_line1", selected.address_line1);
    text("address_line2", selected.address_line2);
    text("city", selected.city);
    text("postcode", selected.postcode);
    text("phone", selected.phone);
    text("email", selected.email);
    text("website", selected.website);
    const numField = (key: "lat" | "lng") => {
      const raw = draft[key].trim();
      const next = raw === "" ? null : Number(raw);
      if (next !== selected[key] && (next === null || Number.isFinite(next))) out[key] = next;
    };
    numField("lat");
    numField("lng");
    const so = parseInt(draft.sort_order, 10);
    if (Number.isFinite(so) && so !== selected.sort_order) out.sort_order = so;
    return out;
  }, [draft, selected]);

  const dirty = Object.keys(fieldPatch).length > 0 || settingsDirty;

  const saveIssue = (): string | null => {
    if (!draft) return null;
    if (!draft.name.trim()) return "A name is required.";
    if (draft.country_code.trim() && draft.country_code.trim().length !== 2) return "A country code is exactly two letters (ISO 3166-1 alpha-2).";
    if (draft.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(draft.email.trim())) return "That email address isn't valid.";
    if (draft.website.trim() && !/^https?:\/\/\S+$/i.test(draft.website.trim())) return "A website needs the scheme too — https://…";
    return null;
  };

  const save = () => {
    if (!selected) return;
    const issue = saveIssue();
    if (issue) { toast({ tone: "error", title: "Fix this first", subtitle: issue }); return; }
    const body: Record<string, unknown> = { ...fieldPatch };
    // You WRITE `settings_json`, you READ `settings` — and the write REPLACES
    // this node's own layer, so the whole layer goes, never a diff.
    if (settingsDirty) body.settings_json = own;
    if (!Object.keys(body).length) return; // an empty body is a 400, not a no-op
    patch(selected, body, `Saved ${selected.name}`);
  };

  const discard = () => {
    if (!selected) return;
    setDraft(toDraft(selected));
    setOwn({ ...selected.settings });
  };

  // ── Scope maths: who a value actually reaches ─────────────────────────────
  const descendants = useMemo(
    () => (selected ? nodes.filter((n) => n.id !== selected.id && n.path.startsWith(selected.path)) : []),
    [nodes, selected],
  );

  const children = useMemo(
    () => (selected ? nodes.filter((n) => n.parent_id === selected.id) : []),
    [nodes, selected],
  );

  /** For one key: which descendants receive THIS node's value, and which cut it
   *  off with their own. Read from each node's own layer in the flat list — the
   *  cascade for the selected node itself still comes from `attribution`. */
  const impact = (k: string) => {
    if (!selected) return { reaches: 0, overrides: [] as UINode[] };
    const overrides = descendants.filter((d) => has(d.settings, k));
    const reaches = descendants.filter((d) => {
      const chain = pathSegments(d.path);
      const from = chain.indexOf(selected.id);
      return !chain.slice(from + 1).some((id) => {
        const n = byId.get(id);
        return !!n && has(n.settings, k);
      });
    });
    return { reaches: reaches.length, overrides };
  };

  const parentOptions = useMemo(() => {
    if (!selected) return [];
    return nodes
      .filter((n) => canDropOn(n.id, selected.id))
      .map((n) => ({ value: n.id, label: n.name, supportingText: `Level ${n.depth} · ${n.code}` }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, selected]);

  const settingKeys = useMemo(() => {
    const keys = new Set([...Object.keys(resolved), ...Object.keys(own)]);
    return [...keys].sort((a, b) => settingLabel(a).localeCompare(settingLabel(b)));
  }, [resolved, own]);

  /** Every key anyone in the tree sets — the compare view's axis. */
  const allKeys = useMemo(() => {
    const keys = new Set<string>();
    nodes.forEach((n) => Object.keys(n.settings).forEach((k) => keys.add(k)));
    return [...keys].sort((a, b) => settingLabel(a).localeCompare(settingLabel(b)));
  }, [nodes]);

  useEffect(() => {
    if (!compareKey && allKeys.length) setCompareKey(allKeys[0]);
  }, [allKeys, compareKey]);

  const kindOptions = kinds.filter((k) => k.active).map((k) => ({ value: k.key, label: k.label, supportingText: k.key }));
  const localeOptions = [...new Set([...(draft ? [draft.locale] : []), ...LOCALES])].filter(Boolean).map((l) => ({ value: l, label: l }));

  const set = (key: keyof Draft, v: string) => setDraft((d) => (d ? { ...d, [key]: v } : d));

  const parent = selected ? byId.get(selected.parent_id ?? "") ?? null : null;
  const selIssues = selected ? issuesByNode.get(selected.id) ?? [] : [];
  const clock = selected ? localTime(selected.timezone, now) : null;

  // ── The compare view: one setting, every node ────────────────────────────
  /** Effective value for any node and key, walking its own path upward. Only
   *  the SELECTED node's cascade comes from the API; a whole-tree column has to
   *  be resolved locally, and it is resolved the same way — nearest ancestor
   *  wins — from the layers the flat list already carries. */
  const effectiveFor = (n: UINode, k: string): { value: unknown; sourceId: string | null } => {
    const chain = pathSegments(n.path);
    for (let i = chain.length - 1; i >= 0; i--) {
      const anc = byId.get(chain[i]);
      if (anc && has(anc.settings, k)) return { value: anc.settings[k], sourceId: anc.id };
    }
    return { value: undefined, sourceId: null };
  };

  const compareRows = useMemo(
    () => [...nodes].sort((a, b) => a.path.localeCompare(b.path)),
    [nodes],
  );

  const compareColumns: TableColumn<UINode>[] = useMemo(() => {
    const k = compareKey ?? "";
    return [
      {
        id: "node",
        header: "Node",
        minWidth: 280,
        cell: (n) => (
          <div style={{ display: "flex", alignItems: "center", gap: T.space[2], paddingLeft: n.depth * 18 }}>
            <span style={{ ...T.text.bodySm, fontWeight: isTenantRoot(n) ? 700 : 600, color: T.n1 }}>{n.name}</span>
            {n.kind ? <Chip>{kindLabel(n.kind)}</Chip> : null}
            {n.status !== "active" ? statusPill(n.status) : null}
          </div>
        ),
      },
      {
        id: "value",
        header: settingLabel(k),
        minWidth: 200,
        cell: (n) => {
          const { value, sourceId } = effectiveFor(n, k);
          if (value === undefined) return <span style={{ ...T.text.bodySm, color: T.soft }}>Not set anywhere above</span>;
          const ownHere = sourceId === n.id;
          return (
            <span style={{ ...T.text.bodySm, fontWeight: ownHere ? 700 : 400, color: ownHere ? T.n1 : T.soft }}>
              {settingPreview(k, value)}
            </span>
          );
        },
      },
      {
        id: "source",
        header: "Where it comes from",
        minWidth: 220,
        cell: (n) => {
          const { sourceId } = effectiveFor(n, k);
          if (!sourceId) return <span style={{ color: T.soft }}>—</span>;
          if (sourceId === n.id) return <StatusPill tone="primary" size="sm">Set here</StatusPill>;
          return <span style={{ ...T.text.label, fontWeight: 400, color: T.soft }}>Inherited from {byId.get(sourceId)?.name ?? sourceId}</span>;
        },
      },
      {
        id: "open",
        header: "",
        align: "right",
        minWidth: 90,
        cell: (n) => (
          <Btn variant="link" size="sm" onClick={() => { setSelectedId(n.id); setTab("settings"); setView("tree"); }}>Edit</Btn>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareKey, byId, kinds]);

  const divergence = useMemo(() => {
    const k = compareKey ?? "";
    const setters = nodes.filter((n) => has(n.settings, k));
    const unset = nodes.filter((n) => effectiveFor(n, k).value === undefined);
    return { setters: setters.length, unset: unset.length };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareKey, nodes]);

  return (
    <Screen rail="nodes">
      <PageHeader
        title="Organisation"
        subtitle="Every place and unit this tenant books through, and the settings each one passes down. Levels are yours to define — the shape is whatever you need, to a depth of 12."
        actions={
          <>
            <Btn variant="secondary" onClick={() => nav.go("nodes.attributes")}>Attributes</Btn>
            <Btn
              icon={<Icon.plus size={13} />}
              disabled={!nodes.length}
              onClick={() => { setAddUnder(selectedId ?? nodes.find(isTenantRoot)?.id ?? null); }}
            >
              Add node
            </Btn>
          </>
        }
      />

      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: T.space[5], padding: `${T.space[5]} ${T.space[6]} ${T.space[6]}` }}>
        {error ? <Banner tone="danger" title="Couldn't load the tree">{error}</Banner> : null}

        {advisory ? (
          <Banner
            effect="consequential"
            title="Saved"
            onDismiss={() => setAdvisory(null)}
            decision={<>
              <b>{advisory.children.length} child {advisory.children.length === 1 ? "node stayed" : "nodes stayed"} active.</b> Deactivating a node deliberately does not cascade — a cascade here would take down a whole region because somebody archived its head office. {advisory.children.map((c) => c.name).join(", ")} {advisory.children.length === 1 ? "remains" : "remain"} active until you change {advisory.children.length === 1 ? "it" : "them"} yourself.
            </>}
          >{advisory.message}</Banner>
        ) : null}

        {/* Tiles that are also the tree's filter — a count you can act on. */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: T.space[4] }}>
          <StatTile
            label="Nodes"
            value={counts.total}
            delta={`${counts.levels} ${counts.levels === 1 ? "level" : "levels"} deep`}
            icon={<Icon.network size={15} />}
            active={filter === "all"}
            detailLabel="Show all"
            onClick={() => { setFilter("all"); setView("tree"); }}
          />
          <StatTile
            label="Need attention"
            value={counts.attention}
            delta={counts.attention ? "Unfinished or inconsistent" : "Nothing outstanding"}
            deltaPos={counts.attention === 0}
            icon={<Icon.warn size={15} />}
            accent={T.orange1}
            active={filter === "attention"}
            detailLabel="Show these"
            onClick={() => { setFilter("attention"); setView("tree"); }}
          />
          <StatTile
            label="Not live"
            value={counts.notActive}
            delta="In setup or deactivated"
            icon={<Icon.clock size={15} />}
            active={filter === "not-active"}
            detailLabel="Show these"
            onClick={() => { setFilter("not-active"); setView("tree"); }}
          />
          <StatTile
            label="Set their own rules"
            value={counts.overrides}
            delta={`${allKeys.length} ${allKeys.length === 1 ? "setting" : "settings"} in use`}
            icon={<Icon.layers size={15} />}
            active={filter === "overrides"}
            detailLabel="Show these"
            onClick={() => { setFilter("overrides"); setView("tree"); }}
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: T.space[4] }}>
          <SegmentedControl
            size="sm"
            options={[{ value: "tree", label: "Structure" }, { value: "compare", label: "Compare a setting" }]}
            value={view}
            onChange={setView}
            ariaLabel="View"
          />
          {filter !== "all" ? (
            <Btn variant="link" size="sm" onClick={() => setFilter("all")}>Clear filter</Btn>
          ) : null}
        </div>

        {view === "compare" ? (
          <Card padding="content" style={{ display: "flex", flexDirection: "column", gap: T.space[4], minHeight: 0 }}>
            <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: T.space[5], flexWrap: "wrap" }}>
              <div style={{ maxWidth: 620 }}>
                <SectionLabel>One setting, every node</SectionLabel>
                <div style={{ ...T.text.bodySm, color: T.soft, marginTop: T.space[1] }}>
                  Bold means the node sets the value itself; grey means it inherits. This is the fastest way to see where behaviour actually varies — and where a value nobody set is quietly missing.
                </div>
              </div>
              <Dropdown
                label="Setting"
                options={allKeys.map((k) => ({ value: k, label: settingLabel(k), supportingText: k }))}
                value={compareKey}
                onChange={setCompareKey}
                placeholder={allKeys.length ? "Choose a setting…" : "No settings in use"}
                portal
                style={{ minWidth: 260 }}
              />
            </div>

            {compareKey ? (
              <>
                <div style={{ display: "flex", gap: T.space[2], flexWrap: "wrap", alignItems: "center" }}>
                  <Chip>{divergence.setters} {divergence.setters === 1 ? "node sets" : "nodes set"} it</Chip>
                  {divergence.unset ? <Chip>{divergence.unset} inherit nothing</Chip> : null}
                  {settingHint(compareKey) ? <span style={{ ...T.text.label, fontWeight: 400, color: T.soft }}>{settingHint(compareKey)}</span> : null}
                </div>
                <Table
                  columns={compareColumns}
                  rows={compareRows}
                  getRowId={(n) => n.id}
                  stickyHeader
                  caption={`${settingLabel(compareKey)} across the organisation`}
                />
              </>
            ) : (
              <Banner effect="inert" title="No settings in use yet">
                Nothing in this tree sets a setting of its own, so there is nothing to compare. Add one on a node and everything below it inherits it.
              </Banner>
            )}
          </Card>
        ) : (
          <div style={{ flex: 1, minHeight: 0, display: "grid", gridTemplateColumns: "minmax(300px, 360px) minmax(0, 1fr)", gap: T.space[5] }}>
            {/* ── The tree ─────────────────────────────────────────────────── */}
            <Card padding={0} style={{ display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
              <div style={{ padding: T.space[4], display: "flex", flexDirection: "column", gap: T.space[2], borderBottom: `1px solid ${T.border}` }}>
                <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a node by name or code" leading={<Icon.search size={14} />} size="sm" full />
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: T.space[3] }}>
                <div style={{ ...T.text.label, color: T.soft, fontWeight: 400 }}>
                  {filter === "all"
                    ? `${counts.total} ${counts.total === 1 ? "node" : "nodes"} · ${counts.levels} ${counts.levels === 1 ? "level" : "levels"} deep`
                    : filter === "attention" ? `Showing the ${counts.attention} that need attention`
                    : filter === "not-active" ? `Showing the ${counts.notActive} that aren't live`
                    : `Showing the ${counts.overrides} with settings of their own`}
                </div>
                <Btn
                  variant="link"
                  size="sm"
                  onClick={() => setExpanded((xs) => (xs.length ? [] : nodes.filter((n) => nodes.some((c) => c.parent_id === n.id)).map((n) => n.id)))}
                >
                  {expanded.length ? "Collapse all" : "Expand all"}
                </Btn>
                </div>
              </div>

              <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: T.space[2] }}>
                <PageState
                  noun="nodes"
                  loading={loading}
                  empty={!nodes.length}
                  filtered={!!matches}
                  variant="inline"
                  skeleton="table"
                  emptyTitle="No nodes yet"
                  emptyBody="This tenant's tree is empty. A tenant has exactly one root — a second one is refused."
                  onClearFilters={() => { setQuery(""); setFilter("all"); }}
                >
                  <FolderTree
                    nodes={treeNodes}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    expandedIds={expanded}
                    onToggle={(id, open) => setExpanded((xs) => (open ? [...xs, id] : xs.filter((x) => x !== id)))}
                    draggableNodes
                    dropTargetId={dropTarget}
                    onNodeDragStart={(id) => setDragId(id)}
                    onNodeDragOver={(id, e) => { if (canDropOn(id, dragId)) { e.preventDefault(); setDropTarget(id); } }}
                    onNodeDrop={(id) => onDrop(id)}
                    renderActions={(n) => {
                      const node = byId.get(n.id);
                      if (!node) return null;
                      return (
                        <MoreMenu
                          size={24}
                          iconSize={13}
                          items={[
                            { label: "Add a node under this one", icon: <Icon.plus size={13} />, onClick: () => setAddUnder(node.id) },
                            "divider",
                            node.status === "active"
                              ? { label: "Deactivate", icon: <Icon.archive size={13} />, onClick: () => patch(node, { status: "inactive" }, `${node.name} is now inactive`) }
                              : { label: "Make active", icon: <Icon.check size={13} />, onClick: () => patch(node, { status: "active" }, `${node.name} is now active`) },
                          ]}
                        />
                      );
                    }}
                    aria-label="Organisation structure"
                  />
                </PageState>
              </div>

              <div style={{ padding: `${T.space[3]} ${T.space[4]}`, borderTop: `1px solid ${T.border}`, ...T.text.label, fontWeight: 400, color: T.soft }}>
                Drag a node onto another to reparent it. Only legal targets accept a drop — a move into a node's own subtree, or one that would pass depth {NODE_DEPTH_CAP}, is never offered.
              </div>
            </Card>

            {/* ── The node workspace ──────────────────────────────────────── */}
            {selected && draft ? (
              <Card padding={0} style={{ display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
                <div style={{ padding: `${T.space[5]} ${T.space[6]} ${T.space[4]}`, display: "flex", flexDirection: "column", gap: T.space[3] }}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: T.space[4] }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ ...T.text.h2, fontWeight: 700, color: T.n1 }}>{selected.name}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: T.space[2], marginTop: T.space[1], flexWrap: "wrap" }}>
                        <span style={{ fontFamily: T.fontMono, fontSize: 12, color: T.soft }}>{selected.code}</span>
                        {selected.kind ? <Chip>{kindLabel(selected.kind)}</Chip> : <StatusPill effect="inert" tone="neutral" size="sm">no kind row</StatusPill>}
                        {statusPill(selected.status)}
                        {clock ? (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, ...T.text.label, fontWeight: 400, color: T.soft }}>
                            <Icon.clock size={12} /> {clock.time} local · {clock.day}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: T.space[2], flexShrink: 0 }}>
                      <Btn variant="secondary" size="sm" icon={<Icon.plus size={12} />} onClick={() => setAddUnder(selected.id)}>Add child</Btn>
                      <MoreMenu
                        items={[
                          selected.status === "active"
                            ? { label: "Deactivate", sub: "Children stay active — this never cascades.", icon: <Icon.archive size={13} />, onClick: () => patch(selected, { status: "inactive" }, `${selected.name} is now inactive`) }
                            : { label: "Make active", icon: <Icon.check size={13} />, onClick: () => patch(selected, { status: "active" }, `${selected.name} is now active`) },
                        ]}
                      />
                    </div>
                  </div>

                  {/* The path, read from `path` rather than walked client-side. */}
                  <div style={{ display: "flex", alignItems: "center", gap: T.space[1], flexWrap: "wrap", ...T.text.label, fontWeight: 400, color: T.soft }}>
                    {pathSegments(selected.path).map((seg, i, all) => (
                      <span key={seg} style={{ display: "inline-flex", alignItems: "center", gap: T.space[1] }}>
                        {i > 0 ? <Icon.chev size={10} dir="right" /> : null}
                        {i === all.length - 1 ? (
                          <span style={{ color: T.n1, fontWeight: 600 }}>{byId.get(seg)?.name ?? seg}</span>
                        ) : (
                          <Btn variant="link" size="sm" onClick={() => setSelectedId(seg)}>{byId.get(seg)?.name ?? seg}</Btn>
                        )}
                      </span>
                    ))}
                  </div>
                </div>

                <div style={{ padding: `0 ${T.space[6]}` }}>
                  <Tabs value={tab} onChange={setTab}>
                    {/* `showZero` is not a prop on the shipped `Tab` (2026-09-08).
                        It never suppressed anything — the design system already
                        omits a zero count — so passing `count` only when there is
                        one is the same rendering with a prop that exists, rather
                        than a prop the component silently ignored. */}
                    <Tab value="overview" {...(selIssues.length ? { count: selIssues.length } : {})}>Overview</Tab>
                    <Tab value="details">Details</Tab>
                    <Tab value="settings" {...(Object.keys(own).length ? { count: Object.keys(own).length } : {})}>Settings</Tab>
                    <Tab value="structure" {...(children.length ? { count: children.length } : {})}>Structure</Tab>
                  </Tabs>
                </div>

                <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: T.space[6], display: "flex", flexDirection: "column", gap: T.space[6] }}>
                  {tab === "overview" ? (
                    <>
                      {selIssues.length ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: T.space[3] }}>
                          <SectionLabel>Needs attention</SectionLabel>
                          {selIssues.map((iss) => (
                            <Banner
                              key={iss.id}
                              tone={iss.tone}
                              title={iss.title}
                              action={iss.tab ? <Btn variant="secondary" size="sm" onClick={() => setTab(iss.tab as string)}>Fix it</Btn> : undefined}
                            >
                              {iss.body}
                            </Banner>
                          ))}
                        </div>
                      ) : (
                        <Banner tone="success" title="Nothing outstanding on this node">
                          Its kind, status and — where the kind is a place — its address and contact details are all in order.
                        </Banner>
                      )}

                      <div>
                        <SectionLabel>How it operates</SectionLabel>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: T.space[4], marginTop: T.space[3] }}>
                          <FactTile
                            icon={<Icon.clock size={14} />}
                            label="Local time"
                            value={clock ? clock.time : selected.timezone}
                            note={clock ? `${selected.timezone}${parent && parent.timezone !== selected.timezone ? ` · differs from ${parent.name}` : ""}` : "That time zone isn't recognised."}
                            flag={!!parent && parent.timezone !== selected.timezone}
                          />
                          <FactTile
                            icon={<Icon.money size={14} />}
                            label="Takes payment in"
                            value={selected.currency}
                            note={parent && parent.currency !== selected.currency ? `differs from ${parent.name} (${parent.currency})` : parent ? `same as ${parent.name}` : "tenant default"}
                            flag={!!parent && parent.currency !== selected.currency}
                          />
                          <FactTile
                            icon={<Icon.globe size={14} />}
                            label="Speaks"
                            value={selected.locale}
                            note={parent && parent.locale !== selected.locale ? `differs from ${parent.name} (${parent.locale})` : parent ? `same as ${parent.name}` : "tenant default"}
                            flag={!!parent && parent.locale !== selected.locale}
                          />
                          <FactTile
                            icon={<Icon.loc size={14} />}
                            label="Where it is"
                            value={selected.city || selected.address_line1 || "Not a place"}
                            note={
                              selected.address_line1 || selected.city
                                ? [selected.address_line1, selected.postcode, selected.country_code].filter(Boolean).join(", ")
                                : kindOf(selected)?.class === "place" ? "This kind is a place — an address is expected." : "This kind is a unit, so no address is needed."
                            }
                          />
                        </div>
                      </div>

                      <div>
                        <SectionLabel>What it governs</SectionLabel>
                        <div style={{ ...T.text.bodySm, color: T.soft, marginTop: T.space[1], maxWidth: 620 }}>
                          {descendants.length
                            ? <>Changes to this node's settings reach <b>{descendants.length} {descendants.length === 1 ? "node" : "nodes"}</b> below it, except where one sets its own value.</>
                            : <>Nothing sits below this node, so its settings apply to it alone.</>}
                        </div>
                        <div style={{ marginTop: T.space[4], display: "flex", flexDirection: "column" }}>
                          {settingKeys.length ? settingKeys.map((k, i) => {
                            const ownHere = has(own, k);
                            const value = ownHere ? own[k] : resolved[k];
                            const src = attribution[k];
                            const from = !ownHere && src && src.source_node_id !== selected.id ? src.source_node_name : null;
                            const { reaches, overrides } = impact(k);
                            return (
                              <div key={k} style={{
                                display: "flex", alignItems: "center", justifyContent: "space-between", gap: T.space[4],
                                padding: `${T.space[3]} 0`,
                                borderTop: i === 0 ? `1px solid ${T.border}` : "none", borderBottom: `1px solid ${T.border}`,
                              }}>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ ...T.text.body, fontWeight: 600, color: T.n1 }}>{settingLabel(k)}</div>
                                  <div style={{ ...T.text.label, fontWeight: 400, color: T.soft, marginTop: 2 }}>
                                    {ownHere
                                      ? <>Set here · reaches {reaches} of {descendants.length} below{overrides.length ? ` · ${overrides.length} override it` : ""}</>
                                      : <>Inherited{from ? ` from ${from}` : ""}{overrides.length ? ` · ${overrides.length} below set their own` : ""}</>}
                                  </div>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: T.space[3], flexShrink: 0 }}>
                                  <span style={{ ...T.text.body, fontWeight: ownHere ? 700 : 400, color: ownHere ? T.n1 : T.soft }}>{settingPreview(k, value)}</span>
                                  <Btn variant="link" size="sm" onClick={() => setTab("settings")}>Change</Btn>
                                </div>
                              </div>
                            );
                          }) : (
                            <Banner effect="inert" title="No settings reach this node">
                              Neither this node nor any ancestor sets one. Add a setting and everything below inherits it.
                            </Banner>
                          )}
                        </div>
                      </div>
                    </>
                  ) : null}

                  {tab === "details" ? (
                    <>
                      <FieldGroup title="Identity" hint="A code and a kind are identity, not description — PATCH declares neither, so neither is editable here.">
                        <Input label="Name" value={draft.name} onChange={(e) => set("name", e.target.value)} full />
                        <Input label="Code" value={selected.code} readOnly helperText="Permanent. Filters, exports and integrations point at it." full />
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: T.n1, marginBottom: 6 }}>Kind</div>
                          <div style={{ display: "flex", alignItems: "center", gap: T.space[2], minHeight: 40 }}>
                            {selected.kind ? <Chip>{kindLabel(selected.kind)}</Chip> : <StatusPill effect="inert" tone="neutral" size="sm">no kind row</StatusPill>}
                            <span style={{ ...T.text.label, fontWeight: 400, color: T.soft }}>
                              {kindOf(selected)?.class === "place" ? "A place — customers go there." : kindOf(selected)?.class === "unit" ? "A unit — it organises, it isn't visited." : "Your own vocabulary."}
                            </span>
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: T.n1, marginBottom: 6 }}>Status</div>
                          <SegmentedControl
                            options={STATUS_OPTIONS.map((s) => ({ value: s.value, label: s.label }))}
                            value={draft.status}
                            onChange={(v) => setDraft((d) => (d ? { ...d, status: v as UINode["status"] } : d))}
                            ariaLabel="Status"
                            size="sm"
                          />
                          <div style={{ ...T.text.label, fontWeight: 400, color: T.soft, marginTop: 6 }}>
                            Deactivating never cascades — children keep their own status.
                          </div>
                        </div>
                      </FieldGroup>


                      <FieldGroup title="Regional" hint="What this node books in: its clock, its language and its money. Anything left as the parent's simply matches it.">
                        <TimeZonePicker label="Time zone" value={draft.timezone} onChange={(v) => set("timezone", v)} />
                        <Dropdown label="Locale" options={localeOptions} value={draft.locale} onChange={(v) => set("locale", v)} portal />
                        <CurrencySelect label="Currency" value={draft.currency} onChange={(v) => set("currency", v)} />
                        <Input label="Country" value={draft.country_code} onChange={(e) => set("country_code", e.target.value)} placeholder="CH" helperText="Two letters, ISO 3166-1." full />
                      </FieldGroup>


                      <FieldGroup
                        title="Where it is"
                        hint={kindOf(selected)?.class === "unit"
                          ? "This kind is a unit, so an address is optional — it organises rather than hosts."
                          : "Customers are sent here, so this is what they see and navigate to."}
                      >
                        <Input label="Address line 1" value={draft.address_line1} onChange={(e) => set("address_line1", e.target.value)} full />
                        <Input label="Address line 2" value={draft.address_line2} onChange={(e) => set("address_line2", e.target.value)} full />
                        <Input label="City" value={draft.city} onChange={(e) => set("city", e.target.value)} full />
                        <Input label="Postcode" value={draft.postcode} onChange={(e) => set("postcode", e.target.value)} full />
                        <Input label="Latitude" value={draft.lat} onChange={(e) => set("lat", e.target.value)} placeholder="47.3769" full />
                        <Input label="Longitude" value={draft.lng} onChange={(e) => set("lng", e.target.value)} placeholder="8.5417" full />
                      </FieldGroup>


                      <FieldGroup title="How to reach it" hint="Shown to customers wherever this node is offered publicly.">
                        <Input label="Phone" value={draft.phone} onChange={(e) => set("phone", e.target.value)} full />
                        <Input label="Email" value={draft.email} onChange={(e) => set("email", e.target.value)} full />
                        <Input label="Website" value={draft.website} onChange={(e) => set("website", e.target.value)} placeholder="https://" full />
                        <Input label="Sort order" type="number" value={draft.sort_order} onChange={(e) => set("sort_order", e.target.value)} helperText="Lower sorts first among its siblings." full />
                      </FieldGroup>
                    </>
                  ) : null}

                  {tab === "settings" ? (
                    <>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: T.space[4] }}>
                        <div>
                          <SectionLabel>Rules this node applies</SectionLabel>
                          <div style={{ ...T.text.bodySm, color: T.soft, marginTop: T.space[1], maxWidth: 560 }}>
                            A value is either set here or inherited from the nearest ancestor that sets it. Editing an inherited value creates an override on this node — and every row says who that change would move.
                          </div>
                        </div>
                        <Btn variant="secondary" size="sm" icon={<Icon.plus size={12} />} onClick={() => setAdding(true)}>Add a setting</Btn>
                      </div>

                      {adding ? (
                        <Card soft padding="compact" style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 150px minmax(0,1fr) auto", gap: T.space[3], alignItems: "flex-end" }}>
                          <Input label="Key" value={newSetting.key} onChange={(e) => setNewSetting((s) => ({ ...s, key: e.target.value }))} placeholder="late_arrival_grace_minutes" size="sm" full />
                          <div>
                            <div style={{ ...T.text.label, color: T.n1, marginBottom: 6 }}>Type</div>
                            <SegmentedControl
                              size="sm"
                              options={[{ value: "text", label: "Text" }, { value: "number", label: "Number" }, { value: "boolean", label: "Yes/no" }]}
                              value={newSetting.kind}
                              onChange={(v) => setNewSetting((s) => ({ ...s, kind: v }))}
                              ariaLabel="Value type"
                            />
                          </div>
                          {newSetting.kind === "boolean" ? (
                            <div>
                              <div style={{ ...T.text.label, color: T.n1, marginBottom: 6 }}>Value</div>
                              <SegmentedControl
                                size="sm"
                                options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                                value={newSetting.value === "true" ? "true" : "false"}
                                onChange={(v) => setNewSetting((s) => ({ ...s, value: v }))}
                                ariaLabel="Value"
                              />
                            </div>
                          ) : (
                            <Input
                              label="Value"
                              type={newSetting.kind === "number" ? "number" : "text"}
                              value={newSetting.value}
                              onChange={(e) => setNewSetting((s) => ({ ...s, value: e.target.value }))}
                              size="sm"
                              full
                            />
                          )}
                          <div style={{ display: "flex", gap: T.space[2] }}>
                            <Btn
                              size="sm"
                              onClick={() => {
                                const key = newSetting.key.trim();
                                if (!/^[a-z][a-z0-9_]*$/.test(key)) { toast({ tone: "error", title: "That key isn't valid", subtitle: "Lower-case, starting with a letter; letters, digits and underscores." }); return; }
                                const value = newSetting.kind === "boolean" ? newSetting.value === "true"
                                  : newSetting.kind === "number" ? Number(newSetting.value)
                                  : newSetting.value;
                                if (newSetting.kind === "number" && !Number.isFinite(value as number)) { toast({ tone: "error", title: "That value isn't a number" }); return; }
                                setOwn((o) => ({ ...o, [key]: value }));
                                setAdding(false);
                                setNewSetting({ key: "", kind: "text", value: "" });
                              }}
                            >Add</Btn>
                            <Btn variant="ghost" size="sm" onClick={() => setAdding(false)}>Cancel</Btn>
                          </div>
                        </Card>
                      ) : null}

                      {settingKeys.length ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: T.space[3] }}>
                          {settingKeys.map((k) => {
                            const ownHere = has(own, k);
                            const value = ownHere ? own[k] : resolved[k];
                            const src = attribution[k];
                            // Read, never re-derived: the response names the ancestor each
                            // value came from, which keeps "set here" distinct from
                            // "happens to agree with its parent".
                            const inheritedFrom = !ownHere && src && src.source_node_id !== selected.id ? src.source_node_name : null;
                            const kind = valueKind(value);
                            const hint = settingHint(k);
                            const unit = SETTING_META[k]?.unit;
                            const { reaches, overrides } = impact(k);
                            return (
                              <Card key={k} padding="compact" soft={ownHere} style={{ display: "flex", flexDirection: "column", gap: T.space[3] }}>
                                <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 240px", gap: T.space[5], alignItems: "flex-start" }}>
                                  <div style={{ minWidth: 0 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: T.space[2], flexWrap: "wrap" }}>
                                      <span style={{ ...T.text.body, fontWeight: 600, color: T.n1 }}>{settingLabel(k)}</span>
                                      {ownHere
                                        ? <StatusPill tone="primary" size="sm">Set here</StatusPill>
                                        : <StatusPill tone="neutral" size="sm">{inheritedFrom ? `From ${inheritedFrom}` : "Inherited"}</StatusPill>}
                                    </div>
                                    {hint ? <div style={{ ...T.text.label, fontWeight: 400, color: T.soft, marginTop: 4 }}>{hint}</div> : null}
                                    <div style={{ ...T.text.label, fontWeight: 400, color: T.soft, marginTop: T.space[2], display: "flex", alignItems: "center", gap: T.space[2], flexWrap: "wrap" }}>
                                      <span style={{ fontFamily: T.fontMono, fontSize: 11 }}>{k}</span>
                                      <span>·</span>
                                      <span>{descendants.length ? `reaches ${reaches} of ${descendants.length} below` : "applies to this node alone"}</span>
                                      {overrides.length ? <span>· {overrides.length} override it ({overrides.slice(0, 2).map((o) => o.name).join(", ")}{overrides.length > 2 ? "…" : ""})</span> : null}
                                    </div>
                                  </div>

                                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: T.space[2] }}>
                                    {kind === "boolean" ? (
                                      <Toggle checked={value === true} onChange={(next) => setOwn((o) => ({ ...o, [k]: next }))} />
                                    ) : kind === "number" ? (
                                      <div style={{ display: "flex", alignItems: "center", gap: T.space[2] }}>
                                        <Input
                                          type="number"
                                          value={String(value ?? "")}
                                          onChange={(e) => {
                                            const n = Number(e.target.value);
                                            setOwn((o) => ({ ...o, [k]: e.target.value === "" ? "" : Number.isFinite(n) ? n : o[k] }));
                                          }}
                                          size="sm"
                                          style={{ width: 110 }}
                                          full={false}
                                        />
                                        {unit ? <span style={{ ...T.text.label, fontWeight: 400, color: T.soft }}>{unit}</span> : null}
                                      </div>
                                    ) : kind === "string" ? (
                                      <Input value={String(value ?? "")} onChange={(e) => setOwn((o) => ({ ...o, [k]: e.target.value }))} size="sm" full />
                                    ) : (
                                      <span style={{ fontFamily: T.fontMono, fontSize: 11.5, color: T.soft, textAlign: "right" }}>{JSON.stringify(value)}</span>
                                    )}
                                    <div style={{ display: "flex", alignItems: "center", gap: T.space[2] }}>
                                      {kind !== "other" ? (
                                        <span style={{ ...T.text.label, fontWeight: 400, color: T.soft }}>{settingPreview(k, value)}</span>
                                      ) : null}
                                      {ownHere ? (
                                        <Btn
                                          variant="link"
                                          size="sm"
                                          onClick={() => setOwn((o) => { const next = { ...o }; delete next[k]; return next; })}
                                        >
                                          {has(resolved, k) && src && src.source_node_id !== selected.id ? "Revert to inherited" : "Remove"}
                                        </Btn>
                                      ) : null}
                                    </div>
                                  </div>
                                </div>
                              </Card>
                            );
                          })}
                        </div>
                      ) : (
                        <Banner effect="inert" title="Nothing configured, nothing inherited">
                          This node has no settings of its own and no ancestor sets any. Add one and everything below this node inherits it.
                        </Banner>
                      )}
                    </>
                  ) : null}

                  {tab === "structure" ? (
                    <>
                      <FieldGroup title="Where it sits" hint="Moving a node rewrites the path and depth of the node and every descendant — it is not a field edit, so it saves on its own.">
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: T.n1, marginBottom: 6 }}>Sits under</div>
                          {isTenantRoot(selected) ? (
                            <div style={{ ...T.text.bodySm, color: T.soft, minHeight: 40, display: "flex", alignItems: "center" }}>
                              Top of the organisation. The tenant root can't be moved, and there can be only one.
                            </div>
                          ) : (
                            <Dropdown
                              options={parentOptions}
                              value={null}
                              onChange={(v) => move(selected.id, v)}
                              placeholder={parent?.name ?? "Pick a new parent"}
                              aria-label="Move under"
                              portal
                            />
                          )}
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: T.n1, marginBottom: 6 }}>Reach</div>
                          <div style={{ ...T.text.bodySm, color: T.n1, minHeight: 40, display: "flex", alignItems: "center" }}>
                            {selected.depth === 0 ? "Top level" : `Level ${selected.depth} of ${NODE_DEPTH_CAP}`}
                            <span style={{ color: T.soft, marginLeft: 6 }}>· {descendants.length} {descendants.length === 1 ? "node" : "nodes"} below</span>
                          </div>
                        </div>
                      </FieldGroup>


                      <div>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: T.space[4], marginBottom: T.space[3] }}>
                          <div>
                            <SectionLabel>Directly under this node</SectionLabel>
                            <div style={{ ...T.text.label, fontWeight: 400, color: T.soft, marginTop: 2 }}>
                              {children.length ? `${children.length} ${children.length === 1 ? "node" : "nodes"}` : "Nothing yet"}
                            </div>
                          </div>
                          <Btn variant="secondary" size="sm" icon={<Icon.plus size={12} />} onClick={() => setAddUnder(selected.id)}>Add a node here</Btn>
                        </div>

                        {children.length ? (
                          <div style={{ display: "flex", flexDirection: "column" }}>
                            {children.map((c, i) => {
                              const cIssues = issuesByNode.get(c.id) ?? [];
                              const cClock = localTime(c.timezone, now);
                              return (
                                <div
                                  key={c.id}
                                  onClick={() => setSelectedId(c.id)}
                                  style={{
                                    display: "flex", alignItems: "center", gap: T.space[3], padding: `${T.space[3]} ${T.space[2]}`,
                                    borderTop: i === 0 ? `1px solid ${T.border}` : "none", borderBottom: `1px solid ${T.border}`, cursor: "pointer",
                                  }}
                                >
                                  <span style={{ color: T.soft, display: "inline-flex" }}><Icon.folder size={14} /></span>
                                  <span style={{ ...T.text.bodySm, fontWeight: 600, color: T.n1, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</span>
                                  {Object.keys(c.settings).length ? <Chip>{Object.keys(c.settings).length} own {Object.keys(c.settings).length === 1 ? "rule" : "rules"}</Chip> : null}
                                  {cIssues.length ? (
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, ...T.text.label, fontWeight: 600, color: T.orange1 }}>
                                      <Icon.warn size={12} />{cIssues.length}
                                    </span>
                                  ) : null}
                                  {cClock && c.timezone !== selected.timezone ? (
                                    <span style={{ ...T.text.label, fontWeight: 400, color: T.soft, fontFamily: T.fontMono, fontSize: 11 }}>{cClock.time}</span>
                                  ) : null}
                                  {c.status !== "active" ? statusPill(c.status) : null}
                                  <span style={{ color: T.soft, display: "inline-flex" }}><Icon.chev size={12} dir="right" /></span>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <Banner effect="inert" title="A leaf, for now">
                            Nothing sits under this node. Anything you add here inherits its settings until it sets its own.
                          </Banner>
                        )}
                      </div>
                    </>
                  ) : null}
                </div>

                {/* One dirty model, one save. The body carries only what changed —
                    an empty PATCH body is a 400, not a no-op. */}
                {dirty ? (
                  <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: T.space[4],
                    padding: `${T.space[3]} ${T.space[6]}`, borderTop: `1px solid ${T.border}`, background: T.cardSoft,
                  }}>
                    <span style={{ ...T.text.bodySm, color: T.soft }}>
                      {Object.keys(fieldPatch).length + (settingsDirty ? 1 : 0)} unsaved {Object.keys(fieldPatch).length + (settingsDirty ? 1 : 0) === 1 ? "change" : "changes"} on {selected.name}
                    </span>
                    <div style={{ display: "flex", gap: T.space[2] }}>
                      <Btn variant="ghost" size="sm" onClick={discard} disabled={saving}>Discard</Btn>
                      <Btn size="sm" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save changes"}</Btn>
                    </div>
                  </div>
                ) : null}
              </Card>
            ) : (
              <Card padding="content" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <EmptyState
                  title="Pick a node"
                  body="Choose a node in the tree to see whether it is ready, what it passes down, and who a change to it would move."
                />
              </Card>
            )}
          </div>
        )}
      </div>

      {/* ── Create ─────────────────────────────────────────────────────────── */}
      <Drawer
        open={addUnder !== null}
        onClose={() => setAddUnder(null)}
        title="Add a node"
        description={addUnder ? <>It will sit under <b>{byId.get(addUnder)?.name ?? "—"}</b> and inherit that node's settings until it sets its own.</> : undefined}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: T.space[2] }}>
            <Btn variant="ghost" onClick={() => setAddUnder(null)}>Cancel</Btn>
            <Btn onClick={createNode} disabled={saving}>{saving ? "Creating…" : "Create node"}</Btn>
          </div>
        }
      >
        <div style={{ display: "grid", gap: T.space[4] }}>
          <Input label="Name" value={newNode.name} onChange={(e) => setNewNode((d) => ({ ...d, name: e.target.value }))} placeholder="e.g. Zürich Hauptbahnhof" full />
          <Input
            label="Code"
            value={newNode.code}
            onChange={(e) => setNewNode((d) => ({ ...d, code: e.target.value }))}
            placeholder="zurich-hb"
            helperText={newNode.code ? undefined : CODE_HINT}
            error={newNode.code ? nodeCodeIssue(newNode.code) ?? undefined : undefined}
            full
          />
          {/* NOT a fixed Region/Location/Virtual list — that vocabulary is not
              the platform's to own. */}
          <Dropdown
            label="Kind"
            options={kindOptions}
            value={newNode.kind || null}
            onChange={(v) => setNewNode((d) => ({ ...d, kind: v }))}
            placeholder={kindsLoading ? "Loading kinds…" : kindOptions.length ? "Choose a kind…" : "No kinds available"}
            helperText={!kindsLoading && !kindOptions.length
              ? "No kinds are defined for this tenant yet."
              : "Kinds are yours to define — this list is your own vocabulary, not a fixed set."}
            portal
          />
          <Divider />
          <TimeZonePicker label="Time zone" value={newNode.timezone} onChange={(v) => setNewNode((d) => ({ ...d, timezone: v }))} />
          <Dropdown label="Locale" options={LOCALES.map((l) => ({ value: l, label: l }))} value={newNode.locale} onChange={(v) => setNewNode((d) => ({ ...d, locale: v }))} portal />
          <CurrencySelect label="Currency" value={newNode.currency} onChange={(v) => setNewNode((d) => ({ ...d, currency: v }))} />
        </div>
      </Drawer>
    </Screen>
  );
}

/** One operational fact, stated as an answer rather than a column. */
function FactTile({ icon, label, value, note, flag }: { icon: React.ReactNode; label: string; value: string; note?: string; flag?: boolean }) {
  return (
    <Card padding="compact" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ display: "flex", alignItems: "center", gap: T.space[2], ...T.text.label, fontWeight: 500, color: T.soft }}>
        <span style={{ display: "inline-flex", color: T.soft }}>{icon}</span>{label}
      </div>
      <div style={{ ...T.text.h3, fontWeight: 700, color: T.n1 }}>{value}</div>
      {note ? (
        <div style={{ ...T.text.label, fontWeight: flag ? 600 : 400, color: flag ? T.n1 : T.soft }}>{note}</div>
      ) : null}
    </Card>
  );
}

/**
 * A titled block of fields inside the editor body: a strong SectionLabel over a
 * description line, then the fields inside ONE Card.
 *
 * The card matters (design review, 2026-08-26). The previous cut put four groups
 * of bare fields in a column, so "Regional" and "Where it is" were told apart
 * only by a 12px overline — three text roles at nearly one weight, and the panel
 * read as a list rather than as a form with parts. Giving each group a surface
 * makes the boundary structural instead of typographic, and it is the Card the
 * design system already ships; no label gutter, no per-group tint.
 */
function FieldGroup({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: T.space[3] }}>
      <div style={{ display: "flex", flexDirection: "column", gap: T.space[1], maxWidth: 640 }}>
        <SectionLabel tone="strong">{title}</SectionLabel>
        {hint ? <div style={{ ...T.text.help, color: T.soft, textWrap: "pretty" }}>{hint}</div> : null}
      </div>
      <Card padding="none">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: T.space[4], padding: T.space[4] }}>
          {children}
        </div>
      </Card>
    </div>
  );
}
